import _ from 'lodash';
import {useCallback, useMemo, useState} from 'react';

import * as CustomRunColorsActions from '../../../state/views/customRunColors/actions';
import type * as CustomRunColorsTypes from '../../../state/views/customRunColors/types';
import type * as CustomRunNamesTypes from '../../../state/views/customRunNames/types';
import * as GroupSelectionsActions from '../../../state/views/groupSelections/actions';
import type * as GroupSelectionsTypes from '../../../state/views/groupSelections/types';
import * as ViewHooks from '../../../state/views/hooks';
import {type Ref} from '../../../state/views/runSet/types';
import type * as SemanticLegendTypes from '../../../state/views/semanticLegend/types';
import * as TempSelectionsActions from '../../../state/views/tempSelections/actions';
import type * as TempSelectionsTypes from '../../../state/views/tempSelections/types';
import {runColor} from '../../../util/colors';
import {getFiltersForQuery} from '../../../util/filterV2';
import {useRampFlagAndOrFilters} from '../../../util/rampFeatureFlags/rampFeatureFlags';
import * as Run from '../../../util/runs';
import * as SM from '../../../util/selectionmanager';
import {type WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {type TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {type JobInfo} from '../../Launch/utilTypes';
import {useWorkspaceSettingsColorPalette} from '../../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {useSemanticLegend} from '../../WorkspaceDrawer/Settings/semanticLegend/useSemanticLegend';

export interface RunSelectorRowHeaderProps {
  className?: string;
  customRunColorsRef: CustomRunColorsTypes.Ref;
  customRunNamesRef: CustomRunNamesTypes.Ref;
  disableRunLink?: boolean;
  entityName: string;
  groupSelectionsRef: GroupSelectionsTypes.Ref;
  isGroup?: boolean; // false if row is individual run, true if it's a group of runs
  isGroupExpanded: boolean;
  isHoveringRow: boolean;
  isLaunchActive?: boolean;
  isLaunchPublicQueueActive?: boolean;
  isSingleMode?: boolean;
  isTableCollapsed?: boolean;
  loadingChildren?: boolean;
  onGroupButtonClick?(): void;
  onSelectRunName?: (runId: string) => void;
  onTempCheckboxClick(): void;
  projectName: string;
  recursionDepth: number;
  row: WBTableRowWithRunSetInfo;
  runSetRef: Ref;
  selectedCount: number;
  selectedRunName?: string;
  semanticLegendRef: SemanticLegendTypes.Ref;
  setLaunchDrawerRow(run: string, jobInfo: JobInfo): void;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;
  stopRun(id: string): Promise<any>; // from withMutations
  tempSelectionsRef: TempSelectionsTypes.Ref;
  truncationType: TruncationType;
}

export function useRunSelectorRowHeaderProps(props: RunSelectorRowHeaderProps) {
  const grouping = ViewHooks.usePartMapped(
    props.groupSelectionsRef,
    groupSelections => groupSelections.grouping
  );
  const expandedRowAddresses = ViewHooks.usePartMapped(
    props.groupSelectionsRef,
    groupSelections => groupSelections.expandedRowAddresses
  );
  const visCheckboxState = ViewHooks.usePartMapped(
    props.groupSelectionsRef,
    groupSelections => {
      return SM.getCheckedState(
        groupSelections,
        props.row,
        props.recursionDepth + 1
      );
    }
  );

  // For some reason TS infers the type as `boolean` without the
  // type cast even though `runIsInBounds()` returns `boolean | null`
  const runIsInBounds = ViewHooks.usePartMapped(
    props.groupSelectionsRef,
    groupSelections => {
      return SM.runIsInBounds(
        groupSelections,
        props.row,
        props.recursionDepth + 1
      );
    }
  ) as boolean | null;

  const tempCheckboxState = ViewHooks.usePartMapped(
    props.tempSelectionsRef,
    state => {
      const tempGroupSelections = {
        grouping,
        selections: state,
        expandedRowAddresses,
      };
      return SM.getCheckedState(
        tempGroupSelections,
        props.row,
        props.recursionDepth + 1
      );
    }
  );

  const toggleSelection = ViewHooks.useViewAction(
    props.groupSelectionsRef,
    GroupSelectionsActions.toggleSelection
  );

  const tempSelectToggle = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectToggle,
    props.tempSelectionsRef,
    props.runSetRef,
    props.row,
    props.recursionDepth + 1
  );

  useRampFlagAndOrFilters();
  const uniqueId = Run.uniqueId(props.row, grouping);
  const runSet = ViewHooks.usePartMaybe(props.runSetRef);
  const storedFilters = ViewHooks.usePartMaybe(runSet?.filtersRef);
  const filters = useMemo(
    () => (storedFilters ? getFiltersForQuery(storedFilters) : undefined),
    [storedFilters]
  );
  const sort = ViewHooks.usePartMaybe(runSet?.sortRef);
  const semanticLegendFromRef = ViewHooks.usePartMaybe(props.semanticLegendRef);
  const semanticLegendSettings = useSemanticLegend(
    props.entityName,
    props.projectName,
    filters,
    sort,
    undefined,
    runSet?.search,
    semanticLegendFromRef
  );
  const colorPaletteOption = useWorkspaceSettingsColorPalette();
  const customRunColor = runColor({
    run: props.row,
    groupKeys: grouping,
    customRunColors: ViewHooks.usePartMapped(
      props.customRunColorsRef,
      runColors => runColors[uniqueId]
    ),
    semanticLegendSettings,
    colorPaletteOption,
  });

  const setCustomRunColor = ViewHooks.useViewAction(
    props.customRunColorsRef,
    CustomRunColorsActions.setCustomRunColor
  );
  const setCustomRunColorCurried = useCallback(
    (color: string) => {
      setCustomRunColor(uniqueId, color);
    },
    [uniqueId, setCustomRunColor]
  );

  const customRunName = ViewHooks.usePartMapped(
    props.customRunNamesRef,
    customRunNames =>
      Run.getCustomRunName(
        props.row.name,
        Boolean(props.isGroup),
        customRunNames
      )
  );

  const groupedBy = grouping[props.recursionDepth];
  const isRunGroup = _.isEqual(groupedBy, {section: 'run', name: 'group'});

  const [stopModalOpen, setStopModalOpen] = useState(false);
  const openStopModal = useCallback(
    () => setStopModalOpen(true),
    [setStopModalOpen]
  );
  const closeStopModal = useCallback(
    () => setStopModalOpen(false),
    [setStopModalOpen]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(
    () => setDeleteModalOpen(true),
    [setDeleteModalOpen]
  );
  const closeDeleteModal = useCallback(
    () => setDeleteModalOpen(false),
    [setDeleteModalOpen]
  );

  const {id} = props.row;
  const {stopRun} = props;
  const stopRunCurried = useCallback(() => stopRun(id), [stopRun, id]);

  return {
    closeDeleteModal,
    closeStopModal,
    customRunColor,
    customRunName,
    setDeleteModalOpen,
    deleteModalOpen,
    groupedBy,
    isRunGroup,
    openDeleteModal,
    openStopModal,
    runIsInBounds,
    setCustomRunColor: setCustomRunColorCurried,
    setLaunchDrawerRow: props.setLaunchDrawerRow,
    stopModalOpen,
    stopRun: stopRunCurried,
    tempCheckboxState,
    tempSelectToggle,
    toggleSelection,
    uniqueId,
    visCheckboxState,
  };
}
